import { useEffect, useRef, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { makeForm } from "@/lib/upload";
import { toast } from "sonner";
import { ChevronDown, Check, Inbox, Hourglass } from "lucide-react";
import { treatName } from "@/components/WhatsAppIcon";
import { useAuth } from "@/context/AuthContext";
import { DocCard } from "@/components/DocCard";
import { useFocusFlash } from "@/lib/useFocusFlash";
import { isCompany } from "@/components/CompanyContact";
import { reasonOf, ownerOf, isMissing, isNa, needsTreat, roleOf, partyOfRole, dm, TONE_RANK, plainName } from "@/lib/docReasons";
import { ArchiveZone } from "@/components/ArchiveZone";
import { BuyerAsks } from "@/components/BuyerData";
import { gpTrata } from "@/components/gpView";

const useRO = () => useAuth().user?.role === "direccao";
const OK_GROUPS = [["vendedor", "Proprietário"], ["agencia", "Agência"], ["comprador", "Comprador"]];
const QUIET = new Set(["optional", "na"]);
const okGroup = (d) => roleOf(d) || "agencia";
const PILL = { red: "bg-red-50 text-red-700 ring-red-200", amber: "bg-amber-50 text-amber-800 ring-amber-200", sky: "bg-sky-50 text-sky-700 ring-sky-200", green: "bg-emerald-50 text-emerald-700 ring-emerald-200", grey: "bg-slate-50 text-slate-500 ring-slate-200" };
const cap1 = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const blocked = (d) => isMissing(d) && (d.checks || []).some((c) => c.status !== "resolvido");

function stateOf(d, r) {
  if (isMissing(d) && !blocked(d) && d.illegible) return ["Ilegível · pedido para repetir", "red"];
  if (isMissing(d) && !blocked(d)) return d.status === "pedido" ? ["Pedido", "sky"] : [d.owner_note ? `Em falta (${d.owner_note})` : "Em falta", "amber"];
  if (QUIET.has(r.code) || d.optional) return [cap1(r.tag) || "Opcional", "grey"];
  if (d.verdict === "validado" || r.tone === "green") return ["Verificado", "green"];
  return [cap1(r.tag) || "Por rever", r.tone === "red" ? "red" : "amber"];
}

// Linha compacta: nome · estado · data · Carregar/Ver; «Ver» abre o detalhe (DocCard) por baixo.
function DocLine({ d, p, focus, card, onPick, ro }) {
  const [open, setOpen] = useState(false);
  const [ref, flash] = useFocusFlash([d.key], focus, () => setOpen(true));
  const r = reasonOf(d);
  const [label, tone] = stateOf(d, r);
  const canUpload = isMissing(d) && !blocked(d) && !ro && onPick && !d.owner_note;
  return (
    <div ref={ref} className={`scroll-mt-32 md:scroll-mt-20 border-b border-slate-100 last:border-0 transition-colors duration-700 ${flash ? "bg-amber-50" : ""}`} data-flash={flash ? "on" : "off"} data-testid={`doc-line-${d.key}`}>
      <div className="flex flex-wrap md:grid md:grid-cols-[minmax(0,1fr)_170px_64px_92px] items-center gap-x-3 gap-y-1 py-2.5">
        <button type="button" onClick={() => setOpen(!open)} className="flex-1 min-w-0 text-left text-[15px] navy-text truncate hover:underline" data-testid={`doc-name-${d.key}`}>{plainName(d)}</button>
        <span className="order-3 md:order-none basis-full md:basis-auto">
          <span className={`inline-flex items-center text-[12px] font-semibold px-2 py-0.5 rounded-full ring-1 ring-inset max-w-full truncate ${PILL[tone]}`} data-testid={`doc-state-${d.key}`}>{tone === "green" && <Check size={12} className="mr-1 shrink-0" />}<span className="truncate">{label}</span></span>
        </span>
        <span className="hidden md:block text-xs text-slate-400 tabular-nums" data-testid={`doc-date-${d.key}`}>{d.status_at ? dm(d.status_at) : ""}</span>
        <span className="md:justify-self-end">
          {canUpload
            ? <button type="button" onClick={() => onPick(d.key)} className="h-8 px-3 rounded-lg text-sm font-semibold border border-slate-300 bg-white navy-text hover:bg-slate-50 transition-colors" data-testid={`doc-upload-${d.key}`}>Carregar</button>
            : !open && <button type="button" onClick={() => setOpen(true)} className="h-8 px-3 rounded-lg text-sm font-semibold navy-text hover:bg-slate-100 transition-colors" data-testid={`doc-view-${d.key}`}>Ver</button>}
        </span>
      </div>
      {open && <div className="pb-3" data-testid={`doc-detail-${d.key}`}>{card(d, r, () => setOpen(false))}</div>}
    </div>
  );
}

function groupTitle(g, p, labels) {
  const party = g === "vendedor" || g === "comprador" ? partyOfRole(p, g) : null;
  const many = ((g === "vendedor" ? p.sellers : p.buyers) || []).filter((x) => x.name).length > 1;
  if (party?.name && !many) return isCompany(party) ? party.name : treatName(party);
  return labels[g] || OK_GROUPS.find(([k]) => k === g)[1];
}

function Groups({ p, docs, labels, line, sub, top }) {
  return OK_GROUPS.map(([g]) => {
    const list = docs.filter((d) => okGroup(d) === g);
    if (!list.length) return null;
    const s = sub && sub(list, g);
    return (
      <div key={g} className="pt-3" data-testid={`doc-group-${g}`}>
        <div className="pb-1 border-b border-slate-200">
          <span className="text-sm font-semibold navy-text" data-testid={`doc-group-title-${g}`}>{groupTitle(g, p, labels)}{s?.length > 0 && <span className="font-normal text-slate-500" data-testid={`doc-group-count-${g}`}> · {s.map(([t, tone], i) => <span key={t} className={COUNT_TONE[tone]}>{i ? " · " : ""}{t}</span>)}</span>}</span>
        </div>
        {top?.[g]}
        {list.map(line)}
      </div>
    );
  });
}

// «N em falta» vem do servidor (tratar.missing_docs): o mesmo número do chip do topo, do Próximo passo e dos Contactos.
const COUNT_TONE = { amber: "text-amber-700 font-medium", red: "text-red-700 font-medium", green: "text-emerald-700" };
const activeSub = (p, okBy = {}) => (list, g) => {
  const md = p.missing_docs?.[g];
  const keys = new Set(md?.keys || []);
  const n = md ? md.n : list.filter((d) => isMissing(d) && !blocked(d)).length;
  const m = list.filter((d) => (md ? !keys.has(d.key) : !(isMissing(d) && !blocked(d)))).length;
  return [n && [`${n} em falta`, "amber"], m && [`${m} com aviso`, "red"], okBy[g] && [`${okBy[g]} ok`, "green"]].filter(Boolean);
};

function ActiveList({ pid, p, docs, focus, card, labels, onChange, okBy }) {
  const ro = useRO() || !!gpTrata(p);
  const [pickKey, setPickKey] = useState(null);
  const fileRef = useRef(null);
  const pick = (k) => { setPickKey(k); setTimeout(() => fileRef.current?.click(), 0); };
  const upload = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f || !pickKey) return;
    try { await api.post(`/processes/${pid}/documents/${pickKey}/upload`, await makeForm([["file", f]])); toast.success("Ficheiro carregado e lido"); onChange(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Falha no carregamento"); }
  };
  const closed = ["escriturado", "sem_venda"].includes(p.status);
  const buyerAsks = closed ? null : <div className="py-2.5 border-b border-slate-100"><BuyerAsks p={p} onDone={onChange} ro={ro} testid="doc-buyer-asks" /></div>;
  const hasBuyerGroup = docs.some((d) => okGroup(d) === "comprador");
  const loose = !closed && !hasBuyerGroup && p.stage !== "angariacao" && (p.buyers || []).some((b) => b.name) && (
    <div className="pt-3" data-testid="doc-group-comprador-asks">
      <div className="pb-1 border-b border-slate-200 text-sm font-semibold navy-text">{groupTitle("comprador", p, labels)}</div>{buyerAsks}
    </div>
  );
  if (!docs.length) return <>{loose}<p className="text-sm text-emerald-700 pt-3" data-testid="treat-zone-empty">Nada em falta nem por rever.</p></>;
  return (
    <div className="scroll-mt-32 md:scroll-mt-20" data-testid="treat-zone">
      <input ref={fileRef} type="file" className="hidden" accept=".pdf,image/*,.heic,.heif,.docx" onChange={upload} data-testid="doc-upload-input" />
      <Groups p={p} docs={docs} labels={labels} sub={activeSub(p, okBy)}
        top={{ comprador: buyerAsks }}
        line={(d) => <DocLine key={d.key} d={d} p={p} focus={focus} card={card} onPick={pick} ro={ro} />} />
      {loose}
    </div>
  );
}

function OkZone({ p, docs, count, focus, card, labels }) {
  const [show, setShow] = useState(false);
  const hit = !!focus && docs.some((d) => d.key === focus.key);
  useEffect(() => { if (hit) setShow(true); }, [hit, focus?.t]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!docs.length) return null;
  return (
    <div className="pt-3" data-testid="ok-zone">
      <button onClick={() => setShow(!show)} className="w-full flex items-center gap-2 py-2.5 text-[15px] font-semibold navy-text border-t border-slate-100" data-testid="ok-toggle">
        <Check size={16} strokeWidth={2.75} className="text-[#15803D]" />{count ? `${count} documento${count === 1 ? "" : "s"} verificado${count === 1 ? "" : "s"}` : `${docs.length} opciona${docs.length === 1 ? "l" : "is"} ou não aplicáve${docs.length === 1 ? "l" : "is"}`}
        <ChevronDown size={16} className={`text-slate-500 transition-transform ${show ? "rotate-180" : ""}`} />
      </button>
      {show && <Groups p={p} docs={docs} labels={labels} line={(d) => <DocLine key={d.key} d={d} p={p} focus={focus} card={card} ro />} />}
    </div>
  );
}

const PHASE_TXT = { cpcv: "para o CPCV", escritura: "para escritura" };

function Fold({ title, icon, testid, children, n }) {
  const [open, setOpen] = useState(false);
  if (!n) return null;
  return (
    <div className="pt-1" data-testid={testid}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 py-2.5 text-[15px] font-semibold text-slate-500 border-t border-slate-100" data-testid={`${testid}-toggle`}>
        {icon}{title} · {n}<ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && children}
    </div>
  );
}

const QuietLine = ({ d, p, extra, testid }) => (
  <div className="py-2 pl-6 border-b border-slate-100 last:border-0 text-[15px]" data-testid={testid}>
    <span className="navy-text">{plainName(d)}</span> <span className="text-slate-500 text-sm">· {ownerOf(d, p).label}{extra ? ` · ${extra}` : ""}</span>
  </div>
);

function NewDocsNotice({ docs }) {
  const fresh = docs.filter((d) => d.portal_new);
  if (!fresh.length) return null;
  const n = fresh.length;
  const ok = fresh.every((d) => d.ai_confirmed || d.verdict === "validado");
  return (
    <div className="mt-3 flex items-center gap-2 text-sm text-slate-600" data-testid="new-docs-notice">
      <Inbox size={15} className="text-slate-400" /> {n === 1 ? "Chegou 1 documento" : `Chegaram ${n} documentos`} pela página do cliente · {ok ? (n === 1 ? "lido e verificado" : "lidos e verificados") : "por verificar"}
    </div>
  );
}

export function DocGroups({ pid, p, docs: allDocs, onChange, focus, Detail, labels = {}, links = [], onHistory }) {
  if (["escriturado", "sem_venda"].includes(p.status)) return <ArchiveZone pid={pid} p={p} docs={allDocs.filter((d) => !d.hidden)} onChange={onChange} />;
  const docs = allDocs.filter((d) => !d.hidden && !d.later);
  const later = allDocs.filter((d) => d.later && !d.hidden);
  const toTreat = docs.filter((d) => needsTreat(d) && (!isMissing(d) || blocked(d)) && !(d.alert?.code === "check_pair" && d.alert?.mirror))
    .sort((a, b) => TONE_RANK[reasonOf(a).tone] - TONE_RANK[reasonOf(b).tone]);
  const missing = docs.filter((d) => !isNa(d) && isMissing(d) && !d.optional && !blocked(d) && d.key !== "contrato_mediacao");
  const ok = docs.filter((d) => isNa(d) || d.verdict === "validado" || d.optional);
  const waiting = ok.filter((d) => d.alert?.code === "waiting");
  const listed = ok.filter((d) => d.alert?.code !== "waiting");
  const verified = p.docs_summary ? p.docs_summary.verified : listed.filter((d) => !QUIET.has(d.alert?.code) && !d.optional).length;
  const okBy = {};
  listed.filter((d) => d.verdict === "validado" && !d.optional && !QUIET.has(d.alert?.code)).forEach((d) => { okBy[okGroup(d)] = (okBy[okGroup(d)] || 0) + 1; });
  const card = (d, r, onCollapse) => <DocCard key={d.key} pid={pid} p={p} doc={d} reason={r} links={links} onChange={onChange} focus={focus} onHistory={onHistory} Detail={Detail} onCollapse={onCollapse} />;
  return (
    <div className="px-6 pb-4" data-testid="doc-groups">
      <NewDocsNotice docs={docs} />
      <ActiveList pid={pid} p={p} docs={[...toTreat, ...missing]} focus={focus} card={card} labels={labels} onChange={onChange} okBy={okBy} />
      <OkZone p={p} docs={listed} count={verified} focus={focus} card={card} labels={labels} />
      <Fold title="Em espera" icon={<Hourglass size={15} />} testid="wait-zone" n={waiting.length}>
        {waiting.map((d) => <QuietLine key={d.key} d={d} p={p} testid={`wait-line-${d.key}`} extra={`${String(d.waiting).charAt(0).toLowerCase()}${String(d.waiting).slice(1)}`} />)}
      </Fold>
      <Fold title="Documentos para mais tarde" testid="later-zone" n={later.length}>
        {later.map((d) => <QuietLine key={d.key} d={d} p={p} testid={`later-line-${d.key}`} extra={PHASE_TXT[d.phase] || "mais tarde"} />)}
      </Fold>
    </div>
  );
}
